<template>
  <div class="workspace" v-if="preLoad">
    <div class="jps-message jps-red workspace-float" v-if="isExpired">
      {{ lss('有効期限が切れています。', 'This workspace has expired') }}
    </div>
    <template v-else>
      <div class="workspace-float">
        <div class="jps-message jps-red" v-if="isNotPublished">
          {{
            lss(
              'このワークスペースは関係者限り公開状態です',
              'You can not access to this workspace',
            )
          }}
        </div>
      </div>
      <div class="need-login" v-if="needLogin">
        <div class="login-area">
          <div class="login-background" aria-hidden="true">
            <div class="bg-image"></div>
          </div>
          <form class="workspace-login" @submit.prevent="manualLogin()">
            <div class="login-body">
              <h3 class="is-size-5 jps-block">
                <BaseIcon name="jps/logo-sq-orange" alt="JapanSearch Logo" size="size-2"/>
                {{
                  isMygallery
                    ? lss(
                        '招待されたギャラリーに入室する',
                        'Join the invited Gallery',
                      )
                    : lss('ワークスペースに入室する', 'Enter to the workspace')
                }}
              </h3>
              <div class="jps-field" v-if="isMygallery">
                <label class="jps-label">{{
                  lss('ギャラリー番号', 'Gallery Number')
                }}</label>
                <input
                  class="jps-input"
                  type="text"
                  v-model="inputWorkspaceId"
                  :disabled="nonPasswordLogin"
                />
              </div>
              <div class="jps-field" v-if="!nonPasswordLogin">
                <label class="jps-label">{{
                  lss('アクセスパスワード', 'Access Password')
                }}</label>
                <input
                  class="jps-input"
                  type="password"
                  v-model="loginPassword"
                />
              </div>
              <h3 class="is-size-7 jps-block">
                <BaseIcon name="mdi/account-circle" size="size-2" />
                {{
                  lss(
                    '入室後に表示するお名前と、カラーを決めてください。',
                    'Set your display name and color in the gallery.',
                  )
                }}
              </h3>
              <div class="jps-field">
                <label class="jps-label">
                  {{ lss(' 入室後に表示するお名前', 'Display Name') }}</label
                >
                <input class="jps-input" type="text" v-model="wsUser.name" />
              </div>
              <div class="jps-field" aria-hidden="true">
                <label class="jps-label">
                  {{ lss(' 何色のアイコンを使いますか？', 'Icon Color') }}
                </label>
                <input
                  class="jps-input jps-account-color"
                  type="color"
                  v-model="wsUser.color"
                />
              </div>
            </div>
            <div class="login-footer">
              <button
                v-if="isMygallery"
                class="jps-round-button cancel-button"
                type="button"
                @click="cancel()"
              >
                {{ ls(dapLangGlobal.cancel) }}
              </button>
              <button
                type="submit"
                :disabled="!loginParamValid"
                class="jps-round-button jps-blue"
              >
                {{ lss('入室する', 'Enter') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </template>
    <Component
      v-if="childComponent && wsState"
      :is="childComponent"
      :ws-state="wsState"
      :ws-user="wsUser"
      :ws-param="wsParam"
      :page="page"
      :meta="meta"
      @update-state="setState"
      @update-location="updateLocation"
      @logout="close(true)"
      @stop-sync="stopSync"
      @resume-sync="resumeSync"/>
  </div>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'

const { updatePageTitle, updateLastBreadcrumbs } = usePage()
const { getTitle } = useSite()
const route = useRoute()
const router = useRouter()
const { ls, lss, langPath } = useI18n()
const { setLoading } = useLoader()
const { snackbarError } = useSnackbar()
const workspace = useWorkspace()
const workspaceStore = useWorkspaceStore()
const workspaceManagerStore = useWorkspaceManagerStore()
const workspaceSessionStore = useWorkspaceSessionStore()

const needLogin: Ref<boolean> = ref(false)
const nonPasswordLogin: Ref<boolean> = ref(false)
const wsState: Ref<WorkspaceState> = ref(null)
const wsUser: Ref<WorkspaceUser> = ref(null)
const wsUserKey: Ref<string> = ref(null)
const inputWorkspaceId: Ref<string> = ref('')
const loginPassword: Ref<string> = ref('')
const isNotPublished = ref(false)
const isExpired = ref(false)
const preLoad = ref(false)

interface Props {
  childComponent?: Component
  page?: Page
  meta?: any
}
const props = withDefaults(defineProps<Props>(), {
})

const isMygallery = computed(() => {
  return route.meta.mygallery
})

const wsId = computed(() => {
  if (isMygallery.value) {
    return 'jpsws-' + inputWorkspaceId.value
  } else {
    return route.params.wsId as string
  }
})

const wsParam = computed(() => {
  return {
    workspaceId: wsId.value,
    user: wsUser.value,
    wsUserKey: wsUserKey.value,
  }
})

///////ログイン関連////////////////////////////////
const stopSync = () => {
  clearInterval(intervalSync)
  clearInterval(intervalHeartbeat)
}

const resumeSync = () => {
  intervalSync = setInterval(sync, 5000)
  intervalHeartbeat = setInterval(heartbeat, 30 * 1000)
}

onMounted(async () => {
  setLoading(true)
  try {
    if (isMygallery.value) {
      //マイギャラリーの場合、ローカルワークスペースに該当するオンラインIDがあるかを確認
      if (route.query.wid) {
        inputWorkspaceId.value = route.query.wid as string
      } else if (workspaceManagerStore.activeOnlineWorkspaceId) {
        inputWorkspaceId.value =
            workspaceManagerStore.activeOnlineWorkspaceId.split('-')[1]
      }
      if (wsId.value) {
        let myws = await workspaceStore.getAll()
        let wse = myws.filter(ws => ws.onlineId === wsId.value).pop()
        if (wse) {
          nonPasswordLogin.value = true
          loginPassword.value = wse.onlineKey
        }
      }
      preLoad.value = true
    } else {
      //ワークスペースの公開状態のチェック
      try {
        const ws = await workspace.getWorkspace(wsId.value)
        isNotPublished.value = ws?.permission?.publicationLevel === 'PERMISSION'
        isExpired.value = ws.expired < new Date().getTime()
        preLoad.value = true
      } catch (e) {
        isNotPublished.value = true
        preLoad.value = true
      }
      //自動ログインの確認(自動ログインの前にやらないと、ログアウト後にこの処理が走らないため、機関ユーザもPWログインになってしまう)
      let ws = await workspace.getWorkspace(wsId.value)
      if (ws.permission.level !== 'PUBLIC') {
        nonPasswordLogin.value = true
      }
    }
    try {
      //既存のセッションを利用してのログイン
      const { state, user } = await workspaceManagerStore.openSession(wsId.value)

      wsUser.value = user
      loginSuccessProcess(state)
      return
    } catch (e) {
      console.error(e)
    }
    //ログインが必要
    if (wsUser.value == null) await initUserInfo()
    needLogin.value = true
  } catch (ex) {
    console.error(ex)
  } finally {
    setLoading(false)
  }
})

const initUserInfo = async () => {
  let session = await workspaceSessionStore.getSession(wsId.value)
  if (session) {
    //セッション情報がある場合、そこから履歴を取得してくる。
    wsUser.value = {
      name: session.name,
      color: session.color,
    }
  } else {
    wsUser.value = {
      name: workspaceManagerStore.favoredName,
      color: workspaceManagerStore.favoredColor,
    }
  }
}

const loginParamValid = computed(() => {
  return (
    wsUser.value.name &&
    wsUser.value.color &&
    (nonPasswordLogin.value || loginPassword.value)
  )
})

const cancel = () => {
  if (isMygallery.value) {
    router.push(langPath('/mygallery'))
  }
}

const manualLogin = async () => {
  setLoading(true)
  try {
    const { state, user } = await workspaceManagerStore.createSession(
      wsId.value,
      loginPassword.value,
      wsUser.value,
    )
    if (!nonPasswordLogin.value) loginPassword.value = null
    wsUser.value = user
    loginSuccessProcess(state)
  } catch (error) {
    console.error(error)
    snackbarError(
      isMygallery.value
        ? {
            ja: '入室できませんでした。ギャラリー番号とアクセスパスワードを確認してください。',
          }
        : { ja: 'ログインに失敗しました。パスワードが間違っていませんか？' },
    )
  } finally {
    setLoading(false)
  }
}

const loginSuccessProcess = (state: WorkspaceState) => {
  needLogin.value = false
  setState(state)
  //マイギャラリーの場合、URLが一つなので、現在のActiveなワークスペースのIDをセットする
  if (isMygallery.value) workspaceManagerStore.activeOnlineWorkspaceId = state.id
  resumeSync()
}

const setState = (state: WorkspaceState) => {
  if (!state.users[wsUser.value.id]) {
    snackbarError({ ja: '切断されました', en: 'Disconnected' })
    close(false)
  } else {
    wsUser.value = clone(state.users[wsUser.value.id])
    if (state.key) {
      wsUserKey.value = state.key
    }
    wsState.value = state
    if (props.page
        && (props.page.id === 'workspace' || props.page.id === 'mygallery-online')
        && props.meta && wsState.value?.workspace) {
      updatePageTitle(props.meta, getTitle(wsState.value.workspace.name))
      useHead(props.meta)
      updateLastBreadcrumbs(props.page, wsState.value.workspace.name)
    }
  }
}

const updateLocation = async (location: string) => {
  wsUser.value.location = location
  await workspace.syncWorkspaceUser({
    ...wsParam.value,
  })
}

const close = async (del: boolean) => {
  if (!needLogin.value || isMygallery.value) {
    try {
      await workspaceManagerStore.closeSession(wsId.value, del)
      if (isMygallery.value) workspaceManagerStore.activeOnlineWorkspaceId = ''
      if (del) {
        await initUserInfo()
      }
    } finally {
      wsState.value = null
      stopSync()
      needLogin.value = true
    }
  }
}

let intervalSync = null
let intervalHeartbeat = null

const heartbeat = async () => {
  await workspace.syncWorkspaceUser(wsParam.value)
}

const sync = async () => {
  try {
    if (wsState.value) {
      let state = await workspace.syncWorkspaceState(wsParam.value)
      setState(state)
    }
  } catch (error) {
    console.error(error)
    snackbarError({ ja: '切断されました', en: 'Disconnected' })
    await close(false)
  }
}

onBeforeUnmount(() => {
  // console.info('clear base interval')
  stopSync()
})
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.workspace {
  min-height: $partial-scroll-height;

  .workspace-float {
    position: absolute;
    z-index: 999999;
    left: 0;
    bottom: 0;
    padding: 6px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: start;

    .jps-message {
      font-size: 0.75rem;
      padding: 0 !important;
    }
  }

  .need-login {
    overflow-y: auto;
    height: 100%;

    .login-area {
      @include global-width-strict();
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: stretch;
      background: $white;
      border-radius: $global-radius;
      box-shadow: $component-shadow;

      .login-background {
        background: $jps-gray-800;
        padding: 3rem;
        border-bottom-left-radius: 6px;
        border-top-left-radius: 6px;

        .bg-image {
          background-image: url($back-img-path + 'BGpattern_mygallery_signin.svg');
          min-width: 312px;
          height: 100%;
        }
      }

      @include is-mobile() {
        .login-background {
          display: none;
        }
      }

      .workspace-login {
        margin-right: auto;

        .login-body {
          padding: 2rem;

          h3 {
            display: flex;
            align-items: center;

            img, svg {
              margin-right: 0.5rem;
            }

            .jps-icon {
              margin-right: 0.5rem;
            }
          }

          .jps-field {
            margin-left: 2.75rem;
          }

          .jps-account-color {
            padding: 0;
            border: none;
            background: none;
            border-radius: 2rem;
          }
        }

        .login-footer {
          padding: 2rem;
          text-align: center;
          margin-left: 2.75rem;

          .cancel-button {
            margin-right: 2rem;
          }

          .jps-round-button {
            min-width: 6rem;
          }
        }
      }
    }
  }
}
</style>
